import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { estimatePayment, money, monthlyPI } from "@/lib/format";

const NumberField = ({ label, value, setValue, suffix, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[13px] font-normal text-neutral-600">{label}</Label>
    <div className="relative">
      <Input type="number" inputMode="decimal" min="0" value={value} onChange={(event) => setValue(Number(event.target.value))} className="h-11 bg-white pr-12 text-[15px]" data-testid={testId} />
      {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-neutral-500">{suffix}</span>}
    </div>
  </div>
);

const balanceAfter = (principal, annualRate, months, termMonths) => {
  const rate = annualRate / 100 / 12;
  if (!rate) return Math.max(0, principal * (1 - months / termMonths));
  const payment = monthlyPI(principal, annualRate, termMonths / 12);
  return Math.max(0, principal * (1 + rate) ** months - payment * (((1 + rate) ** months - 1) / rate));
};

/** Compares a simple rent path to ownership costs and projected equity; not a loan quote or investment advice. */
export const RentVsBuyCalculator = ({ testId = "rent-vs-buy" }) => {
  const [price, setPrice] = useState(400000);
  const [rent, setRent] = useState(2500);
  const [down, setDown] = useState(10);
  const [years, setYears] = useState(5);
  const [appreciation, setAppreciation] = useState(3);
  const [rentGrowth, setRentGrowth] = useState(3);
  const result = useMemo(() => {
    const payment = estimatePayment({ price, downPct: down, ratePct: 6.5, years: 30, loanType: "conventional", vaUse: "first", taxPct: 1, insuranceMonthly: 125, hoaMonthly: 0 });
    const months = years * 12;
    const futureValue = price * (1 + appreciation / 100) ** years;
    const balance = balanceAfter(payment.loan, 6.5, months, 360);
    const equity = Math.max(0, futureValue - balance);
    const rentPaid = Array.from({ length: years }, (_, i) => rent * 12 * (1 + rentGrowth / 100) ** i).reduce((sum, item) => sum + item, 0);
    return { payment, futureValue, equity, rentPaid };
  }, [price, rent, down, years, appreciation, rentGrowth]);

  return (
    <div className="grid gap-8 rounded-md border border-neutral-200 bg-white p-6 sm:p-8 lg:grid-cols-[1.1fr_1fr]" data-testid={testId}>
      <div>
        <p className="eyebrow">Rent versus buy</p><h3 className="mt-2 font-display text-3xl text-ink">See the paths side by side.</h3>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <NumberField label="Home price" value={price} setValue={setPrice} suffix="$" testId={`${testId}-price`} />
          <NumberField label="Monthly rent" value={rent} setValue={setRent} suffix="$/mo" testId={`${testId}-rent`} />
          <NumberField label="Down payment" value={down} setValue={setDown} suffix="%" testId={`${testId}-down`} />
          <NumberField label="Compare over" value={years} setValue={setYears} suffix="years" testId={`${testId}-years`} />
          <NumberField label="Home value growth (example)" value={appreciation} setValue={setAppreciation} suffix="%/yr" testId={`${testId}-growth`} />
          <NumberField label="Rent increase (example)" value={rentGrowth} setValue={setRentGrowth} suffix="%/yr" testId={`${testId}-rent-growth`} />
        </div>
      </div>
      <div className="rounded-md bg-mist p-6" data-testid={`${testId}-results`}>
        <p className="text-[13px] uppercase tracking-[0.14em] text-neutral-500">At year {years}</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <div><p className="text-[13px] text-neutral-600">Projected owner equity</p><p className="mt-1 font-display text-4xl text-ink" data-testid={`${testId}-equity`}>{money(result.equity)}</p><p className="mt-1 text-[12px] text-neutral-500">Projected value {money(result.futureValue)} less estimated loan balance.</p></div>
          <div><p className="text-[13px] text-neutral-600">Estimated rent paid</p><p className="mt-1 font-display text-4xl text-ink" data-testid={`${testId}-rent-paid`}>{money(result.rentPaid)}</p><p className="mt-1 text-[12px] text-neutral-500">Using your annual rent increase example.</p></div>
        </div>
        <div className="mt-6 border-t border-neutral-200 pt-5"><p className="text-[14px] text-ink">Estimated ownership payment: <strong className="font-medium">{money(result.payment.total)}/mo</strong></p><p className="mt-2 text-[12px] leading-relaxed text-neutral-500">Illustrative only. Ownership also has maintenance, closing and selling costs; values and rents can rise or fall. Ask us for a move-specific plan.</p></div>
      </div>
    </div>
  );
};